import { Injectable, Injector } from '@angular/core';
import { ToastService } from '@core/toast.service';

/**
 * Phase 6 Fix: Removed SwUpdate dependency to enforce single SW ownership
 *
 * This service now uses only native service-worker APIs (navigator.serviceWorker)
 * instead of Angular's SwUpdate class. This ensures:
 * - Single source of truth: Custom service-worker.js
 * - No mixed ownership model
 * - Predictable update behavior
 *
 * Migration path:
 * - checkForUpdate() -> navigator.serviceWorker.getRegistration()
 * - activateUpdate() -> registration.waiting.postMessage({ type: 'SKIP_WAITING' })
 *
 * NOTE: This service is deprecated. Use NativeSwUpdateService instead.
 * Kept for backward compatibility only.
 */
@Injectable({
    providedIn: 'root'
})
export class SwForceUpdateService {

    private static readonly STORAGE_KEY = 'sw_force_update_count';
    private static readonly MAX_ATTEMPTS = 1;
    private injector: Injector;

    constructor(
        injector: Injector
    ) {
        this.injector = injector;
    }

    private get toastService(): ToastService {
        return this.injector.get(ToastService);
    }

    async attemptForceUpdate(): Promise<boolean> {
        const count = this.getAttemptCount();
        if (count >= SwForceUpdateService.MAX_ATTEMPTS) {
            console.warn('[SwForceUpdateService] Max update attempts reached. Update will be handled automatically.');
            return false;
        }
        this.incrementAttemptCount();
        await this.forceSwUpdateAndReload();
        return true;
    }

    isFirestorePermissionDenied(error: any): boolean {
        if (!error) return false;

        if (error.code === 'permission-denied' && error.name === 'FirebaseError') {
            return true;
        }

        if (typeof error.message === 'string'
            && error.message.includes('Missing or insufficient permissions')) {
            return true;
        }

        return false;
    }

    resetAttemptCount(): void {
        sessionStorage.removeItem(SwForceUpdateService.STORAGE_KEY);
    }

    private getAttemptCount(): number {
        const raw = sessionStorage.getItem(SwForceUpdateService.STORAGE_KEY);
        return raw ? parseInt(raw, 10) : 0;
    }

    private incrementAttemptCount(): void {
        const count = this.getAttemptCount() + 1;
        sessionStorage.setItem(SwForceUpdateService.STORAGE_KEY, count.toString());
    }

    private async forceSwUpdateAndReload(): Promise<void> {

        this.toastService.infoToast('Updating app...');

        try {
            if (!('serviceWorker' in navigator)) {
                window.location.reload();
                return;
            }

            const registration = await navigator.serviceWorker.getRegistration();

            if (!registration) {
                window.location.reload();
                return;
            }

            await registration.update();

            let reloadTriggered = false;
            let fallbackTimeoutId: number | null = null;

            const controllerChangeListener = () => {
                reloadTriggered = true;
                if (fallbackTimeoutId !== null) {
                    clearTimeout(fallbackTimeoutId);
                }
                window.location.reload();
            };

            navigator.serviceWorker.addEventListener('controllerchange', controllerChangeListener, { once: true });

            const startFallbackReload = () => {
                fallbackTimeoutId = window.setTimeout(() => {
                    if (!reloadTriggered) {
                        navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeListener);
                        window.location.reload();
                    }
                }, 3000);
            };

            if (registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                startFallbackReload();
            } else if (registration.installing) {
                const installingWorker = registration.installing;
                const stateChangeHandler = () => {
                    if (installingWorker.state === 'installed' && registration.waiting) {
                        installingWorker.removeEventListener('statechange', stateChangeHandler);
                        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                        startFallbackReload();
                    } else if (installingWorker.state === 'redundant') {
                        installingWorker.removeEventListener('statechange', stateChangeHandler);
                        navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeListener);
                        window.location.reload();
                    }
                };
                installingWorker.addEventListener('statechange', stateChangeHandler);
                window.setTimeout(() => {
                    if (!reloadTriggered && !registration.waiting) {
                        installingWorker.removeEventListener('statechange', stateChangeHandler);
                        navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeListener);
                        window.location.reload();
                    }
                }, 5000);
            } else {
                navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeListener);
                window.location.reload();
                return;
            }

        } catch (err) {
            window.location.reload();
        }
    }
}
